import { Component, inject, signal } from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { BehaviorSubject, catchError, EMPTY, map, switchMap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { AsyncPipe } from '@angular/common';
import OrganizationService from 'src/app/shared/services/organization.service';
import { DkCurrencyPipe } from 'src/app/shared/pipes/dk-currency';
import { KickbackResponse, CreateKickbackRequest, EditKickbackRequest } from '../../organization-models';
import { SnackbarService, FeedbackInfoComponent } from 'ok-angular-shared/components';
import { FeatureFlagService } from 'src/app/shared/services/feature-flag.service';
import { FEATURE_FLAGS } from 'src/app/shared/services/feature-flags.const';
import { MatTooltip } from '@angular/material/tooltip';
import { PriceUnitInputComponent } from 'src/app/shared/components/price-unit-input/price-unit-input.component';
export interface KickbackModalData {
  organizationId: number;
  organizationName: string;
}

export interface KickbackForm {
  name: FormControl<string>;
  normalRate: FormControl<number | null>;
  fastRate: FormControl<number | null>;
  superRate: FormControl<number | null>;
}

@Component({
  selector: 'adm-kickback-modal',
  templateUrl: './kickback-modal.component.html',
  styleUrl: './kickback-modal.component.scss',
  imports: [
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    AsyncPipe,
    DkCurrencyPipe,
    MatTooltip,
    PriceUnitInputComponent,
    FeedbackInfoComponent,
  ],
})
export class KickbackModalComponent {
  private organizationService = inject(OrganizationService);
  private featureFlagService = inject(FeatureFlagService);
  private snackbarService = inject(SnackbarService);

  data = inject<KickbackModalData>(MAT_DIALOG_DATA);

  editingKickback = signal<KickbackResponse | null>(null);

  refreshSubject$ = new BehaviorSubject<void>(undefined);
  kickbacks$ = this.refreshSubject$.pipe(
    switchMap(() =>
      this.organizationService.getKickbacks(this.data.organizationId).pipe(
        catchError((err: HttpErrorResponse) => {
          switch (err.error?.code) {
            case 100101:
              this.snackbarService.error(`Organisationen med id ${this.data.organizationId} blev ikke fundet`);
              break;
            default:
              this.snackbarService.error('Der opstod en fejl ved indlæsning af kickback-politikker');
          }
          return EMPTY;
        })
      )
    ),
    map(response => response.kickbacks)
  );

  kickbackFormGroup = new FormGroup<KickbackForm>({
    name: new FormControl<string>('', { validators: [Validators.required], nonNullable: true }),
    normalRate: new FormControl<number | null>(null, { validators: [Validators.required] }),
    fastRate: new FormControl<number | null>(null, { validators: [Validators.required] }),
    superRate: new FormControl<number | null>(null, { validators: [Validators.required] }),
  });

  create(): void {
    if (this.kickbackFormGroup.invalid) return;

    const formData = this.kickbackFormGroup.value;
    const request: CreateKickbackRequest = {
      kickbackName: formData.name!,
      normalRate: formData.normalRate!,
      fastRate: formData.fastRate!,
      superRate: formData.superRate!,
    };

    this.organizationService.createKickback(this.data.organizationId, request).subscribe({
      next: () => {
        this.snackbarService.success(`Kickback-politikken '${request.kickbackName}' er blevet oprettet`);
        this.kickbackFormGroup.reset();
        this.kickbackFormGroup.markAsPristine();
        this.kickbackFormGroup.markAsUntouched();
        this.refreshSubject$.next();
      },
      error: (err: HttpErrorResponse) => {
        switch (err.error?.code) {
          case 100101:
            this.snackbarService.error(`Organisationen med id ${this.data.organizationId} blev ikke fundet`);
            break;
          case 904001:
            this.snackbarService.error('Der eksisterer allerede en kickback-politik med det navn');
            break;
          default:
            this.snackbarService.error('Der opstod en fejl ved oprettelse af kickback-politik');
        }
      },
    });
  }

  edit(kickback: KickbackResponse): void {
    this.editingKickback.set(kickback);
    this.kickbackFormGroup.patchValue({
      name: kickback.name,
      normalRate: kickback.normalRateInKronerExVat,
      fastRate: kickback.fastRateInKronerExVat,
      superRate: kickback.superRateInKronerExVat,
    });
  }

  saveEdit(): void {
    const editing = this.editingKickback();
    if (this.kickbackFormGroup.invalid || !editing) return;

    const formData = this.kickbackFormGroup.value;
    const request: EditKickbackRequest = {
      kickbackName: formData.name!,
      normalRate: formData.normalRate!,
      fastRate: formData.fastRate!,
      superRate: formData.superRate!,
    };

    this.organizationService.editKickback(this.data.organizationId, editing.id, request).subscribe({
      next: () => {
        this.snackbarService.success(`Kickback-politikken '${request.kickbackName}' er blevet opdateret`);
        this.editingKickback.set(null);
        this.kickbackFormGroup.reset();
        this.refreshSubject$.next();
      },
      error: (err: HttpErrorResponse) => {
        switch (err.error?.code) {
          case 100101:
            this.snackbarService.error(`Organisationen med id ${this.data.organizationId} blev ikke fundet`);
            break;
          case 904001:
            this.snackbarService.error('Der eksisterer allerede en kickback-politik med det navn');
            break;
          case 904041:
            this.snackbarService.error('Kickback-politikken blev ikke fundet');
            break;
          default:
            this.snackbarService.error('Der opstod en fejl ved redigering af kickback-politik');
        }
      },
    });
  }

  cancelEdit(): void {
    this.editingKickback.set(null);
    this.kickbackFormGroup.reset();
    this.kickbackFormGroup.markAsPristine();
    this.kickbackFormGroup.markAsUntouched();
  }

  softDelete(kickbackId: number): void {
    this.organizationService.deleteKickback(this.data.organizationId, kickbackId).subscribe({
      next: () => {
        this.snackbarService.success('Kickback-politik slettet');
        this.refreshSubject$.next();
      },
      error: (err: HttpErrorResponse) => {
        switch (err.error?.code) {
          case 100101:
            this.snackbarService.error(`Organisationen med id ${this.data.organizationId} blev ikke fundet`);
            break;
          case 904041:
            this.snackbarService.error('Kickback-politikken kunne ikke slettes');
            break;
          default:
            this.snackbarService.error('Der opstod en fejl ved sletning af kickback-politik');
        }
      },
    });
  }
}
